import { type App, inject } from 'vue';
import { useUserStore } from '@/plugins/pinia/userStore.ts';

declare global {
  interface Window {
    dataLayer: unknown[][];
    gtag: (...args: unknown[]) => void;
  }
}

const GoogleTagManagerSymbol = Symbol('GoogleTagManager');

let googleTagManagerLoaded = false;

export const loadGoogleTagManager = () => {
  if (googleTagManagerLoaded) {
    return;
  }

  const user = useUserStore();

  if (!user.analyticsConsent) {
    console.warn('Google Tag Manager was not initialized because cookie consent was not given.');
    return undefined;
  }

  const measurementId = import.meta.env.VITE_GTM_CONTAINER_ID;
  if (!measurementId) {
    console.warn('Google Tag Manager Container ID was not found.');
    return undefined;
  }

  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtm.js?id=${measurementId}`;
  script.async = true;
  script.crossOrigin = 'anonymous';
  document.head.appendChild(script);

  script.onload = () => {
    window.gtag('config', measurementId, { send_page_view: false });
    console.log('Google Tag Manager initialized.');
  };

  googleTagManagerLoaded = true;
};

const createGoogleTagManager = () => {
  return {
    trackPageView(path: string, title: string) {
      window.gtag?.('event', 'page_view', {
        page_path: path,
        page_title: title,
      });
    },
    trackSearch(searchTerm: string) {
      window.gtag?.('event', 'search', {
        search_term: searchTerm,
      });
    },
    trackArticleProjectClick(articleId: number, projectId: number | null) {
      window.gtag?.('event', 'article_project_click', {
        article_id: articleId,
        project_id: projectId,
      });
    },
    trackArticleShare(articleId: number, method: string) {
      window.gtag?.('event', 'share', {
        method,
        content_type: 'article',
        item_id: String(articleId),
      });
    },
  };
};

export const installGoogleTagManager = (app: App) => {
  const ga = createGoogleTagManager();
  app.provide(GoogleTagManagerSymbol, ga);
};

export function useGoogleTagManager() {
  if (import.meta.env.SSR) {
    return undefined;
  }
  return inject(GoogleTagManagerSymbol) as ReturnType<typeof createGoogleTagManager> | undefined;
}
